/* Cacao bars product page.

   Two jobs, both progressive enhancement:
     1. put `.cacao-ayllu` on <html> so the page tint follows the chosen pack
     2. re-render our own variant-dependent markup when the variant changes

   WHY (2) EXISTS
   Horizon has no `variant:changed` event. assets/variant-picker.js fetches the
   product through the Section Rendering API and morphs the result in, then
   dispatches StandardEvents.productSelect carrying a promise that resolves
   with the freshly rendered HTML. Horizon's own price, gallery, SKU and
   inventory components each subscribe to that event and pull their new
   markup out of `detail.html` -- see assets/product-price.js.

   Our price line is a plain block, so nothing re-rendered it: clicking a pack
   updated the tile and the URL but left the price frozen at whatever the page
   loaded with. Landing on ?variant=<ayllu> and clicking Starter showed
   "$99.00 Free shipping" over a selected Starter tile. This subscribes to the
   same event and swaps every [data-cacao-live] node for its counterpart in
   the new HTML, matched on the block id.

   Taking the markup from the server rather than formatting money in JS keeps
   currency, market and selling-plan pricing correct for free.

   This is a module so it can import Shopify's event name instead of hard
   coding the string, exactly as Horizon's own components do. If it fails to
   load the page still works: every price is server-rendered for the variant
   in the URL, the radios are real radios, and the page is simply the cream
   state throughout. */

import { StandardEvents } from '@shopify/events';

const SCOPE = '[data-cacao-scope]';
const INPUT = 'input[data-cacao-pack]';
const TINT = '[data-cacao-tint]';
const LIVE = '[data-cacao-live]';

const root = document.documentElement;

/* ---------------------------------------------------------------
   Stay on the shop's primary domain.

   Horizon re-renders the product section by fetching the product URL on
   whatever host the page is on. On the myshopify host Shopify answers that
   fetch with a 301 to the primary domain, and the redirected response is
   rendered by the LIVE theme -- which has no cacao-bars template. Shopify
   therefore ignores view=cacao-bars and returns the default variant picker,
   which Horizon morphs in: the pack tiles turn into a plain "Size" radio list
   and Add to cart reads "Unavailable". One tap was enough.

   Nothing in the theme can stop a host-level redirect, so the page moves to
   the primary domain up front, keeping every query parameter (view, variant,
   country, preview_theme_id, UTMs). Guarded by a session flag so a wrong
   canonical can never loop.
   --------------------------------------------------------------- */
(function keepPrimaryDomain() {
  const el = document.querySelector('[data-cacao-canonical]');
  const canonical = el?.getAttribute('data-cacao-canonical');
  if (!canonical) return;
  let target;
  try {
    target = new URL(canonical);
  } catch {
    return;
  }
  if (target.origin === window.location.origin) return;
  try {
    if (sessionStorage.getItem('cacaoHostHop') === '1') return;
    sessionStorage.setItem('cacaoHostHop', '1');
  } catch {
    return; /* no storage, no guard, so do not risk a loop */
  }
  window.location.replace(new URL(window.location.pathname + window.location.search, target).href);
})();

/** Mirror the chosen Ayllu tint (a | b) onto <html>; the tint sits above <body>. */
function syncTint() {
  const el = document.querySelector(TINT);
  root.classList.toggle('cacao-tint-b', el?.getAttribute('data-cacao-tint') === 'b');
}

function applyPack(input) {
  root.classList.toggle('cacao-ayllu', input?.getAttribute('data-cacao-dark') === 'true');
}

/**
 * One-time vs subscribe.
 *
 * Every figure this moves around was rendered by Liquid from the selling
 * plan, so the money is formatted by Shopify for the right market and this
 * only chooses which already-correct string is on screen. Nothing is
 * calculated here, which is why the box cannot drift from what is charged.
 *
 * The selling_plan <select> is the single source of truth for the cart: it is
 * disabled for one-time, which means no plan is submitted at all.
 */
/* The purchase box is re-rendered by Horizon on every variant change, and the
   server always renders one-time checked (that is the no-JavaScript safe
   default). Without remembering the shopper's own choice, picking Subscribe
   and then switching pack silently dropped them back to one-time. */
let chosenMode = null;
let chosenPlan = null;

function restoreChoice(box) {
  if (!box) return;
  const want = chosenMode || box.getAttribute('data-cacao-default') || 'onetime';
  const radio = box.querySelector(`[data-cacao-mode="${want}"]`);
  if (radio && !radio.checked) radio.checked = true;

  const select = box.querySelector('[data-cacao-plan]');
  if (select && chosenPlan && [...select.options].some((o) => o.value === chosenPlan)) {
    select.value = chosenPlan;
  }
}

function syncPurchaseMode() {
  const box = document.querySelector('[data-cacao-buy]');
  restoreChoice(box);
  const mode = box?.querySelector('[data-cacao-mode]:checked')?.value ?? 'onetime';
  const subscribing = mode === 'subscribe';
  const select = box?.querySelector('[data-cacao-plan]');

  if (select) select.disabled = !subscribing;
  if (box) box.classList.toggle('is-subscribing', subscribing);
  for (const card of document.querySelectorAll('.cacao-buy__card')) {
    const on = card.querySelector('[data-cacao-mode]')?.checked === true;
    card.classList.toggle('is-on', on);
  }

  const option = select?.selectedOptions?.[0];
  const planPrice = option?.dataset.price || '';
  const interval = option?.dataset.interval || '';
  const oncePrice = box?.getAttribute('data-once-price') || '';

  /* The subscribe card's headline price follows the chosen frequency. All
     three plans are the same 10% today, but that is a setting in the app and
     could stop being true. */
  const subFig = box?.querySelector('[data-cacao-sub-price]');
  if (subFig && planPrice) subFig.textContent = planPrice;

  /* The big price line. */
  const prices = document.querySelectorAll('[data-cacao-price]');
  if (prices.length) {
    const wanted = subscribing && select?.value ? `plan-${select.value}` : 'onetime';
    let shown = false;
    for (const el of prices) {
      const match = el.getAttribute('data-cacao-price') === wanted;
      el.hidden = !match;
      shown ||= match;
    }
    if (!shown) {
      for (const el of prices) el.hidden = el.getAttribute('data-cacao-price') !== 'onetime';
    }
  }
  for (const el of document.querySelectorAll('[data-cacao-price-save]')) el.hidden = !subscribing;

  /* The per-cup line on each pack tile. */
  for (const el of document.querySelectorAll('[data-cacao-percup]')) {
    el.hidden = el.getAttribute('data-cacao-percup') !== (subscribing ? 'subscribe' : 'onetime');
  }

  /* Horizon's sticky bar renders its own one-time price and knows nothing
     about selling plans, so it is corrected here rather than left to
     contradict the box the reader just used. */
  const sticky = document.querySelector('.sticky-add-to-cart__price');
  if (sticky) {
    if (!sticky.dataset.cacaoOnce) sticky.dataset.cacaoOnce = sticky.innerHTML;
    if (subscribing && planPrice) sticky.textContent = planPrice;
    else sticky.innerHTML = sticky.dataset.cacaoOnce;
  }

  /* The renewal line under Add to cart. */
  for (const el of document.querySelectorAll('[data-cacao-renew]')) {
    if (!subscribing || !planPrice || !interval) {
      el.hidden = true;
      continue;
    }
    /* The plan option reads "Deliver every 4 weeks", which gives
       "Renews at $44.10 deliver every 4 weeks" -- so drop the leading verb
       and keep just the interval. */
    const every = interval.replace(/^\s*deliver\s+/i, '').toLowerCase();
    const tpl = el.getAttribute('data-template') || 'Renews at [price] [interval].';
    el.textContent = tpl.replace('[price]', planPrice).replace('[interval]', every);
    el.hidden = false;
  }

  void oncePrice;
}

function sync() {
  const scope = document.querySelector(SCOPE);
  if (!scope) return;
  syncTint();
  applyPack(scope.querySelector(`${INPUT}:checked`));
  syncPurchaseMode();
}

/* Delegated: the picker is morphed, so a direct binding would work once. */
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof Element)) return;
  if (input.matches(INPUT)) applyPack(input);
  if (input.matches('[data-cacao-mode]')) {
    chosenMode = input.value;
    syncPurchaseMode();
  }
  if (input.matches('[data-cacao-plan]')) {
    chosenPlan = input.value;
    syncPurchaseMode();
  }
});

/**
 * Swap our variant-dependent markup for the server's new version.
 * @param {{ promise: Promise<{ detail: { html?: Document } }> }} event
 */
function handleProductSelect(event) {
  if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

  event.promise
    .then(({ detail }) => {
      if (!detail?.html) return;
      for (const node of document.querySelectorAll(LIVE)) {
        const id = node.getAttribute('data-cacao-live');
        const fresh = detail.html.querySelector(`[data-cacao-live="${CSS.escape(id)}"]`);
        /* Only ever replace. An earlier version removed nodes with no
           counterpart, which emptied the price out of the page whenever the
           section render came back short -- a stale price is recoverable, a
           deleted one is not. */
        if (fresh) node.replaceWith(fresh);
      }
      sync();
    })
    .catch(() => {
      /* The section fetch failed; the page keeps the last good markup. */
    });
}

/* ---------------------------------------------------------------
   Clarity events

   Only ever reach Clarity if the tag is on the page, and the tag is only
   rendered on the cacao-bars product template -- so on any other page these
   listeners exist and do nothing.
   --------------------------------------------------------------- */

function clarityEvent(name) {
  try {
    if (typeof window.clarity === 'function') window.clarity('event', name);
  } catch {
    /* never let analytics break the page */
  }
}

function bindClarityEvents() {
  /* Fired when Shopify confirms the line was added, not when the button is
     pressed, so a failed add is not counted as one. */
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (event.action !== 'add') return;
    event.promise
      ?.then(({ detail }) => {
        if (!detail?.didError) clarityEvent('add_to_cart');
      })
      .catch(() => {});
  });

  /* Once per page: a reader toggling back and forth should not look like
     several people choosing a subscription. */
  let sentSubscribe = false;
  document.addEventListener('change', (event) => {
    const el = event.target;
    if (sentSubscribe || !(el instanceof Element)) return;
    if (el.matches('[data-cacao-mode]') && el.value === 'subscribe' && el.checked) {
      sentSubscribe = true;
      clarityEvent('subscribe_selected');
    }
  });
}

function bind() {
  const section = document.querySelector(SCOPE)?.closest('.shopify-section');
  if (!section || section.dataset.cacaoBound === 'true') return;
  section.dataset.cacaoBound = 'true';
  section.addEventListener(StandardEvents.productSelect, handleProductSelect);
  bindClarityEvents();
}

/* The scope element survives the morph, but the section can be re-hydrated,
   so re-bind and re-read the checked input after the DOM settles. */
if (window.MutationObserver) {
  const scope = document.querySelector(SCOPE);
  if (scope) {
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        bind();
        sync();
      });
    }).observe(scope, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    bind();
    sync();
  });
} else {
  bind();
  sync();
}
